import React from 'react';
import {observer} from 'mobx-react';

import {MOBILE_MAX_SCREEN_SIZE} from 'base/search.js';
import autobind from 'common/decorators/autobind.js';
import {
    PRAC_LOC_TYPE,
    NO_RESULT_WITH_SKINNED_TEXT,
    RESULT_WITH_SKINNED_TEXT,
} from 'core/constants.js';
import {DirectoryStoreContextV2} from 'core/stores/RootStore.js';
import PracticeLocationV2 from 'directory/v2/components/PracticeLocationV2.js';
import ProfileCardV2 from 'directory/v2/components/ProfileCardV2.js';
import NoResultsNotice from 'directory/components/NoResultsNotice.js';
import {NotificationV2} from '@HealthShareAU/hs-component-library';

export default
@observer
class SearchResultsV2 extends React.Component {
    static contextType = DirectoryStoreContextV2;

    constructor(props) {
        super(props);
        this.loadMoreTargetRef = React.createRef();
        // Infinite Scroll
        this.observer = new window.IntersectionObserver(this.loadMore, {
            threshold: 0,
        });
    }

    componentDidUpdate() {
        if (this.loadMoreTargetRef?.current) {
            this.observer.observe(this.loadMoreTargetRef.current);
        }
    }

    componentWillUnmount() {
        this.observer.disconnect();
    }

    isMobile() {
        return window.matchMedia(`(max-width: ${MOBILE_MAX_SCREEN_SIZE})`)
            .matches;
    }

    @autobind
    loadMore(entries) {
        const {locationSearchAPI} = this.context;
        entries.forEach((entry) => {
            if (entry.isIntersecting && !locationSearchAPI.isLoading) {
                if (locationSearchAPI.next) {
                    locationSearchAPI.loadMoreResults();
                }
            }
        });
    }

    renderResult(pracProps) {
        if (pracProps.type === PRAC_LOC_TYPE) {
            return <PracticeLocationV2 {...pracProps} />;
        } else {
            return <ProfileCardV2 {...pracProps} />;
        }
    }

    renderDistanceHeader(index) {
        const {
            specialty,
            rootStore: {
                paramStore: {isReferrals, isProUser, hasSkin},
                healthFundStore: {healthFundThemeColor},
            },
        } = this.context;

        const specialtyName =
            isReferrals || isProUser
                ? specialty.pluralProfessionalName
                : specialty.pluralName;
        let heading = `More ${specialtyName}`;
        let body = `These ${specialtyName} are more than 150km away.`;

        if (index === 0) {
            heading = `There are no ${specialtyName} close to your search location`;
            body = `These ${specialtyName} are more than 150km away, but may still be the closest to you.`;
        }

        return (
            <NotificationV2
                colorTheme={
                    hasSkin && healthFundThemeColor
                        ? healthFundThemeColor
                        : null
                }
                customClass="location"
                customIcon="markerPinV2"
                htmlText={
                    '<strong>More practitioners</strong> These practices are more than 150km away.'
                }
                type="success"
            />
        );
    }

    renderResults() {
        const {
            bannerAdsHaveLoaded,
            interest,
            results,
            specialty,
            telehealthDirectory,
            rootStore: {
                healthFundStore: {healthFund},
                paramStore: {isReferrals},
            },
        } = this.context;

        const distanceHeaderPosition = results.findIndex(
            (prac) => prac.distance > 150,
        );

        if (bannerAdsHaveLoaded) {
            return results?.map((practitioner, i) => {
                const pracProps = {
                    ...practitioner,
                    healthFund,
                    isReferrals,
                    specialty,
                    interest,
                };

                return (
                    <React.Fragment key={practitioner.id}>
                        {distanceHeaderPosition === i &&
                            !telehealthDirectory &&
                            this.renderDistanceHeader(i)}
                        {this.renderResult(pracProps)}
                        {i === results.length - 15 && (
                            <li ref={this.loadMoreTargetRef}></li>
                        )}
                    </React.Fragment>
                );
            });
        }
    }

    render() {
        const {
            bannerAdsHaveLoaded,
            locationSearchAPI,
            results,
            searchResultsError,
            rootStore: {
                paramStore: {isClient, hasSkin},
            },
        } = this.context;
        const {isLoading} = locationSearchAPI;

        const hfInfoHidden = hasSkin && !isClient;
        const NoResultText = hasSkin
            ? RESULT_WITH_SKINNED_TEXT
            : NO_RESULT_WITH_SKINNED_TEXT;

        if (searchResultsError) {
            return (
                <NoResultsNotice>
                    <p>
                        {'An error has occurred while loading this content. '}
                        <a href={window.location.pathname}>
                            {'Please try again'}
                        </a>
                    </p>
                </NoResultsNotice>
            );
        }
        if (results?.length === 0 && !isLoading && bannerAdsHaveLoaded) {
            return (
                <NotificationV2 customClass="no-result" text={NoResultText} />
            );
        }

        const props = {element: 'search-results'};
        if (this.isMobile()) {
            props.numCells = 1;
        }

        return (
            <section aria-busy={isLoading}>
                <ul>{this.renderResults()}</ul>
            </section>
        );
    }
}
